import Vue from 'vue';
import Router from 'vue-router';
import RedirectUtils from './utils';
import AutoLogin from '@/components/views/AutoLogin';
import Login from '@/components/views/Login';
import CompanyForm from '@/components/views/CompanyForm';
import Dashboard from '@/components/views/Dashboard';
import Integrations from '@/components/views/Integrations';
import NoPermissions from '@/components/views/NoPermissions';
import Settings from '@/components/views/Settings';
import SettingsGdpr from '@/components/views/SettingsGdpr';
import SettingsPlugin from '@/components/views/SettingsPlugin';
import SettingsReservations from '@/components/views/SettingsReservations';
import SettingsSms from '@/components/views/SettingsSms';
import Splashscreen from '@/components/views/Splashscreen';
import Register from '@/components/views/Register';
import OutlookAuth from '@/components/views/OutlookAuth';
import PasswordReminder from '@/components/views/PasswordReminder';
import PasswordReset from '@/components/views/PasswordReset';
import SanitizeUtils from '@/components/helpers/SanitizeUtils';

// Lazy loaded parts
const Attendance = () => import('@/components/views/Attendance.vue');
const BlockedTermins = () => import('@/components/views/BlockedTermins.vue');
const Billing = () => import('@/components/views/Billing.vue');
const Calendar = () => import('@/components/views/Calendar.vue');
const Customers = () => import('@/components/views/Customers.vue');
const Discounts = () => import('@/components/views/Discounts.vue');
const DiscountsForm = () => import('@/components/views/DiscountsForm.vue');
const GoogleCalendarConnectForWorker = () => import('@/components/views/GoogleCalendarConnectForWorker.vue');
const Help = () => import('@/components/views/Help.vue');
const History = () => import('@/components/views/History.vue');
const Invoices = () => import('@/components/views/Invoices.vue');
const MyPage = () => import('@/components/views/MyPage.vue');
const News = () => import('@/components/views/News.vue');
const OpeningHours = () => import('@/components/views/OpeningHours.vue');
const OpeningHoursWorkerForm = () => import('@/components/views/OpeningHoursWorkerForm');
const OpeningHoursCompanyForm = () => import('@/components/views/OpeningHoursCompanyForm');
const OutlookCalendarConnectForWorker = () => import('@/components/views/OutlookCalendarConnectForWorker.vue');
const Payments = () => import('@/components/views/Payments.vue');
const PaymentMethodForm = () => import('@/components/views/PaymentMethodForm.vue');
const PriceRules = () => import('@/components/views/PriceRules.vue');
const PriceRulesForm = () => import('@/components/views/PriceRulesForm.vue');
const ReservationForm = () => import('@/components/views/ReservationForm/template.vue');
const ReservationRules = () => import('@/components/views/ReservationRules.vue');
const ReservationRulesForm = () => import('@/components/views/ReservationRulesForm.vue');
const ReservationParameters = () => import('@/components/views/ReservationParameters.vue');
const ReservationParametersForm = () => import('@/components/views/ReservationParametersForm.vue');
const Services = () => import('@/components/views/Services.vue');
const ServicesForm = () => import('@/components/views/ServicesForm/template.vue');
const SettingsEmails = () => import('@/components/views/SettingsEmails/template.vue');
const SettingsGoogleCalendar = () => import('@/components/views/SettingsGoogleCalendar.vue');
const SettingsOutlookCalendar = () => import('@/components/views/SettingsOutlookCalendar.vue');
const SettingsFakturownia = () => import('@/components/views/SettingsFakturownia.vue');
const SettingsGetResponse = () => import('@/components/views/SettingsGetResponse.vue');
const SettingsMultilanguage = () => import('@/components/views/SettingsMultilanguage.vue');
const SettingsPayments = () => import('@/components/views/SettingsPayments.vue');
const SettingsWww = () => import('@/components/views/SettingsWww.vue');
const SmsHistory = () => import('@/components/views/SmsHistory.vue');
const Statistics = () => import('@/components/views/Statistics.vue');
const Surveys = () => import('@/components/views/Surveys.vue');
const SurveysFilled = () => import('@/components/views/SurveysFilled.vue');
const SurveyForm = () => import('@/components/views/SurveyForm.vue');
const WaitingList = () => import('@/components/views/WaitingList.vue');
const WorkerForm = () => import('@/components/views/WorkerForm.vue');
const Workers = () => import('@/components/views/Workers.vue');
const WorkersForm = () => import('@/components/views/WorkersForm.vue');

// Avoid NavigationDuplicated errors
const originalPush = Router.prototype.push;

Router.prototype.push = function push (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }

  return originalPush.call(this, location).catch(error => {
    if (error && error.name !== 'NavigationDuplicated') {
      throw error;
    }
  });
}

Vue.use(Router);

let router = new Router({
  mode: 'history',
  scrollBehavior () {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('dashboard', next);
      }
    },
    {
      path: '/dashboard/welcome',
      name: 'Dashboard - Welcome',
      component: Dashboard,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('dashboard', next);
      }
    },
    {
      path: '/loading',
      name: 'Splashscreen',
      component: Splashscreen
    },
    {
      path: '/login',
      name: 'Login',
      component: Login
    },
    {
      path: '/login/:case',
      name: 'LoginAfterPasswordReset',
      component: Login
    },
    {
      path: '/auto-login/:token',
      name: 'AutoLogin',
      component: AutoLogin
    },
    {
      path: '/password/reminder',
      name: 'PasswordReminder',
      component: PasswordReminder
    },
    {
      path: '/password/reset/:token',
      name: 'PasswordReset',
      component: PasswordReset
    },
    {
      path: '/password/reset',
      name: 'PasswordResetWithoutToken',
      component: PasswordReset
    },
    {
      path: '/register',
      name: 'Register',
      component: Register
    },
    {
      path: '/register/:package_id',
      name: 'RegisterWithPackage',
      component: Register
    },
    {
      path: '/reservation/new/',
      name: 'ReservationFormAdd',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/from-search',
      name: 'ReservationFormAddFromSearch',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/from-blocked-date',
      name: 'ReservationFormAddFromBlockedDate',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/customer/:id',
      name: 'ReservationFormAddCustomer',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/date/:date/:time',
      name: 'ReservationFormAddCustomerDateTime',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/date/:date/:time/:worker',
      name: 'ReservationFormAddCustomerDateTimeAndWorker',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/new/date/:date/:time/:worker/:service',
      name: 'ReservationFormAddCustomerDateTimeAndWorkerAndService',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.systemExpirationCheck(next);
      }
    },
    {
      path: '/reservation/edit/:id',
      name: 'ReservationFormEdit',
      component: ReservationForm,
      meta: { sidebarLevel1: 'home' }
    },
    {
      path: '/calendar',
      name: 'Calendar',
      component: Calendar,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'calendar' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('calendar', next);
      }
    },
    {
      path: '/calendar/:year/:month/:day',
      name: 'CalendarDay',
      component: Calendar,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'calendar' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('calendar', next);
      }
    },
    {
      path: '/company',
      name: 'Company',
      component: CompanyForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'company' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('company_details', next);
      }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/settings/notifications/emails',
      name: 'SettingsEmails',
      component: SettingsEmails,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/settings/gdpr',
      name: 'SettingsGdpr',
      component: SettingsGdpr,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/settings/google-calendar',
      name: 'SettingsGoogleCalendar',
      component: SettingsGoogleCalendar,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('google_calendar', next);
      }
    },
    {
      path: '/settings/google-calendar/connect',
      name: 'SettingsGoogleCalendarConnect',
      component: SettingsGoogleCalendar,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('google_calendar', next);
      }
    },
    {
      path: '/google-calendar-connect',
      name: 'GoogleCalendarConnectForWorker',
      component: GoogleCalendarConnectForWorker,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('google_calendar', next);
      }
    },
    {
      path: '/settings/outlook-calendar',
      name: 'SettingsOutlookCalendar',
      component: SettingsOutlookCalendar,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('outlook_calendar', next);
      }
    },
    {
      path: '/settings/outlook-calendar/connect',
      name: 'SettingsOutlookCalendarConnect',
      component: SettingsOutlookCalendar,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('outlook_calendar', next);
      }
    },
    {
      path: '/outlook-calendar-connect',
      name: 'OutlookCalendarConnectForWorker',
      component: OutlookCalendarConnectForWorker,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('outlook_calendar', next);
      }
    },
    {
      path: '/settings/fakturownia',
      name: 'SettingsFakturownia',
      component: SettingsFakturownia,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('fakturownia_api', next);
      }
    },
    {
      path: '/settings/getresponse',
      name: 'SettingsGetResponse',
      component: SettingsGetResponse,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('getresponse', next);
      }
    },
    {
      path: '/settings/multilanguage',
      name: 'SettingsMultilanguage',
      component: SettingsMultilanguage,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('multilang', next);
      }
    },
    {
      path: '/settings/payments',
      name: 'SettingsPayments',
      component: SettingsPayments,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('payment', next);
      }
    },
    {
      path: '/settings/payments/add/:type',
      name: 'PaymentMethodFormAdd',
      component: PaymentMethodForm,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('payment', next);
      }
    },
    {
      path: '/settings/payments/edit/:id',
      name: 'PaymentMethodFormEdit',
      component: PaymentMethodForm,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('payment', next);
      }
    },
    {
      path: '/settings/plugin',
      name: 'SettingsPlugin',
      component: SettingsPlugin,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/settings/reservations',
      name: 'SettingsReservations',
      component: SettingsReservations,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/settings/sms',
      name: 'SettingsSms',
      component: SettingsSms,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('sms', next);
      }
    },
    {
      path: '/settings/www',
      name: 'SettingsWww',
      component: SettingsWww,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'settings' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/services',
      name: 'Services',
      component: Services,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'services' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('services', next);
      }
    },
    {
      path: '/services/add/:category_id?',
      name: 'ServicesAddForm',
      component: ServicesForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'services' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('services', next);
      }
    },
    {
      path: '/services/edit/:id',
      name: 'ServicesEditForm',
      component: ServicesForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'services' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('services', next);
      }
    },
    {
      path: '/my-page',
      name: 'MyPage',
      component: MyPage,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'my-page' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('my_page', next);
      }
    },
    {
      path: '/billing',
      name: 'Billing',
      component: Billing,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'billing' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('billing', next);
      }
    },
    {
      path: '/reservation/parameters',
      name: 'ReservationParameters',
      component: ReservationParameters,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'reservation-parameters' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_parameters', next);
      }
    },
    {
      path: '/reservation/parameters/add',
      name: 'ReservationParametersAdd',
      component: ReservationParametersForm,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'reservation-parameters' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_parameters', next);
      }
    },
    {
      path: '/reservation/parameters/edit/:id',
      name: 'ReservationParametersEdit',
      component: ReservationParametersForm,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'reservation-parameters' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_parameters', next);
      }
    },
    {
      path: '/history',
      name: 'History',
      component: History,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'history' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('history', next);
      }
    },
    {
      path: '/blocked-termins',
      name: 'BlockedTermins',
      component: BlockedTermins,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'blocked-termins' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('history', next);
      }
    },
    {
      path: '/attendance',
      name: 'Attendance',
      component: Attendance,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'attendance' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('attendances', next);
      }
    },
    {
      path: '/attendance/:year/:month/:day',
      name: 'AttendanceDay',
      component: Attendance,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'attendance' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('attendances', next);
      }
    },
    {
      path: '/sms-history',
      name: 'SMS History',
      component: SmsHistory,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'sms-history' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('sms_history', next);
      }
    },
    {
      path: '/surveys',
      name: 'Surveys',
      component: Surveys,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'surveys' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('surveys', next);
      }
    },
    {
      path: '/surveys/add',
      name: 'SurveyFormAdd',
      component: SurveyForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'surveys' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('surveys', next);
      }
    },
    {
      path: '/surveys/edit/:id',
      name: 'SurveyFormEdit',
      component: SurveyForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'surveys' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('surveys', next);
      }
    },
    {
      path: '/surveys-filled',
      name: 'SurveysFilled',
      component: SurveysFilled,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'surveys-filled' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('surveys', next);
      }
    },
    {
      path: '/payments',
      name: 'Payments',
      component: Payments,
      meta: { sidebarLevel1: 'payment', sidebarLevel2: 'payments' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('payment', next);
      }
    },
    {
      path: '/invoices',
      name: 'Invoices',
      component: Invoices,
      meta: { sidebarLevel1: 'payment', sidebarLevel2: 'invoices' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('invoices', next);
      }
    },
    {
      path: '/workers',
      name: 'Workers',
      component: Workers,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'workers' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('workers', next);
      }
    },
    {
      path: '/workers/add',
      name: 'WorkersFormAdd',
      component: WorkersForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'workers' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('workers', next);
      }
    },
    {
      path: '/workers/edit/:id',
      name: 'WorkersFormEdit',
      component: WorkersForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'workers' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('workers', next);
      }
    },
    {
      path: '/customers',
      name: 'Customers',
      component: Customers,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'customers' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      }
    },
    {
      path: '/rules/prices/',
      name: 'PriceRules',
      component: PriceRules,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'price-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('price_rules', next);
      }
    },
    {
      path: '/rules/prices/add/',
      name: 'PriceRulesAdd',
      component: PriceRulesForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'price-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('price_rules', next);
      }
    },
    {
      path: '/rules/prices/edit/:id',
      name: 'PriceRulesEdit',
      component: PriceRulesForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'price-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('price_rules', next);
      }
    },
    {
      path: '/rules/opening-hours/',
      name: 'OpeningHours',
      component: OpeningHours,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'opening-hours' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck(['company_rules', 'can_manege_exceptions', 'can_manage_worker_exceptions'], next);
      }
    },
    {
      path: '/rules/opening-hours/worker/add/',
      name: 'OpeningHoursWorkerFormAdd',
      component: OpeningHoursWorkerForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'opening-hours' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck(['company_rules', 'can_manage_worker_exceptions'], next);
      }
    },
    {
      path: '/rules/opening-hours/worker/edit/:id',
      name: 'OpeningHoursWorkerFormEdit',
      component: OpeningHoursWorkerForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'opening-hours' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck(['company_rules', 'can_manage_worker_exceptions'], next);
      }
    },
    {
      path: '/rules/opening-hours/company/add/',
      name: 'OpeningHoursCompanyFormAdd',
      component: OpeningHoursCompanyForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'opening-hours' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck(['company_rules', 'can_manege_exceptions'], next);
      }
    },
    {
      path: '/rules/opening-hours/company/edit/:id',
      name: 'OpeningHoursCompanyFormEdit',
      component: OpeningHoursCompanyForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'opening-hours' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck(['company_rules', 'can_manege_exceptions'], next);
      }
    },
    {
      path: '/rules/reservations/',
      name: 'ReservationRules',
      component: ReservationRules,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'reservation-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_rules', next);
      }
    },
    {
      path: '/rules/reservations/add/',
      name: 'ReservationRulesAdd',
      component: ReservationRulesForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'reservation-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_rules', next);
      }
    },
    {
      path: '/rules/reservations/edit/:id',
      name: 'ReservationRulesEdit',
      component: ReservationRulesForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'reservation-rules' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('reservation_rules', next);
      }
    },
    {
      path: '/discounts/',
      name: 'Discounts',
      component: Discounts,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'discounts' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('discounts', next);
      }
    },
    {
      path: '/discounts/add/',
      name: 'DiscountsFormAdd',
      component: DiscountsForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'discounts' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('discounts', next);
      }
    },
    {
      path: '/discounts/edit/:id',
      name: 'DiscountsFormEdit',
      component: DiscountsForm,
      meta: { sidebarLevel1: 'rules', sidebarLevel2: 'discounts' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('discounts', next);
      }
    },
    {
      path: '/integrations/',
      name: 'Integrations',
      component: Integrations,
      meta: { sidebarLevel1: 'settings', sidebarLevel2: 'integrations' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('integrations', next);
      }
    },
    {
      path: '/no-permissions/',
      name: 'NoPermissions',
      component: NoPermissions,
      meta: { sidebarLevel1: 'home' }
    },
    {
      path: '/outlook-auth/',
      name: 'OutlookAuth',
      component: OutlookAuth,
      meta: {}
    },
    {
      path: '/your-profile',
      name: 'WorkerForm',
      component: WorkerForm,
      meta: { sidebarLevel1: 'company', sidebarLevel2: 'your-profile' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.userIsWorkerOrReceptionist(next);
      }
    },
    {
      path: '/help',
      name: 'Help',
      component: Help,
      meta: {}
    },
    {
      path: '/waiting-list',
      name: 'WaitingList',
      component: WaitingList,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'waiting-list' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('waiting_list', next);
      }
    },
    {
      path: '/waiting-list/:year/:month/:day',
      name: 'WaitingListWithDate',
      component: WaitingList,
      meta: { sidebarLevel1: 'calendar', sidebarLevel2: 'waiting-list' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('waiting_list', next);
      }
    },
    {
      path: '/statistics',
      name: 'Statistics',
      component: Statistics,
      meta: { sidebarLevel1: 'stats' },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('stats', next);
      }
    },
    {
      path: '/news',
      name: 'News',
      component: News
    }
  ]
});

router.beforeEach((to, from, next) => {
  let notLoggedInRoutes = [
    'AutoLogin',
    'Login',
    'LoginAfterPasswordReset',
    'PasswordReminder',
    'PasswordReset',
    'PasswordResetWithoutToken',
    'Register',
    'RegisterWithPackage'
  ];

  if (typeof window.ga !== 'undefined' && typeof to !== 'undefined' && to.path !== '/loading') {
    window.ga('set', 'page', to.path);
    window.ga('send', 'pageview');
  }

  if (notLoggedInRoutes.indexOf(to.name) === -1 && !SanitizeUtils.getToken()) {
    let splashscreenHtml = document.querySelector('#splashscreen');

    if (splashscreenHtml) {
      splashscreenHtml.parentNode.removeChild(splashscreenHtml);
    }

    next('/login');
    return;
  }

  if (notLoggedInRoutes.indexOf(to.name) !== -1) {
    let splashscreenHtml = document.querySelector('#splashscreen');

    if (splashscreenHtml) {
      splashscreenHtml.parentNode.removeChild(splashscreenHtml);
    }

    next();
    return;
  }

  if (!window.bookero) {
    setTimeout(() => {
      next({
        name: 'Splashscreen',
        params: {
          nextRoute: to.path
        }
      });

      let splashscreenHtml = document.querySelector('#splashscreen');

      if (splashscreenHtml) {
        splashscreenHtml.parentNode.removeChild(splashscreenHtml);
      }
    }, 0);

    return;
  }

  let currentTime = new Date().getTime();
  let lastVisit = SanitizeUtils.getLocalStorageValue('bookero-last-visit-time', 'integer', 0);

  if (currentTime - lastVisit > 4 * 60 * 60 * 1000) {
    if (notLoggedInRoutes.indexOf(to.name) === -1) {
      next('/login');
    } else {
      next();
    }

    return;
  }

  localStorage.setItem('bookero-last-visit-time', currentTime);

  if (window.bookero) {
    window.bookero.updateFaviconCounter();
  }

  next();
});

router.afterEach((to, from) => {
  let toParts = to.path.split('/')
  let fromParts = from.path.split('/');

  if (
    toParts.length !== fromParts.length ||
    (
      toParts.length >= 1 &&
      fromParts.length >= 1 &&
      toParts[0] !== fromParts[0]
    ) ||
    (
      toParts.length >= 2 &&
      fromParts.length >= 2 &&
      toParts[0] !== fromParts[0] &&
      toParts[1] !== fromParts[1]
    )
  ) {
    setTimeout(() => {
      document.querySelector('body').scrollTo(0, 0);
    }, 100);
  }
});

router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    window.bookero.showLoadingChunkAlert(error);
  }
})

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js